import { computed, ref, watch, watchEffect } from "vue";
import { createGlobalState } from "@vueuse/core";
import { compareDesc, parseISO } from "date-fns";
import i18n from "@/i18n";
import { formatISO } from "date-fns";
import useApplicationsState from "./applicationsState";
import useAuthState from "@/composables/state/authState";
import { usePromiseWrapper } from "@/composables/common";
import usePlatformScreenState from "@/composables/state/platformScreenState";
import { ChatSessionHttpService } from "@/api/http/ChatSessionHttpService";
import { ChatRole } from "@/api/http/schemas/chatSessionSchemas";
import type { Application } from "@/api/http/schemas/applicationSchemas";
import type {
  ChatMessage,
  ChatSession,
  MessageContent,
  MessageContentBlock,
} from "@/api/http/schemas/chatSessionSchemas";
import { ChatPrivateChannel } from "@/api/ws/channels/ChatPrivateChannel";
import { ChatPublicChannel } from "@/api/ws/channels/ChatPublicChannel";
import AiAgentResponseReceivedEvent from "@/api/ws/events/AiAgentResponseReceivedEvent";
import { createFileBlock, buildMessageContent } from "@/util/file";
import { WelcomePageType } from "@/api/http/schemas/borrowerPlatformTemplatesSchemas";

type SessionSettings = {
  agentId: ChatSession["agent_id"];
  applicationId?: Application["id"];
  initialMessage?: ChatMessage;
};

const welcomeMessage = {
  content: {
    version: "1.0.0" as const,
    content: [
      {
        type: "text" as const,
        text: i18n.global.t("chat.welcomeMessage"),
      },
    ],
  },
  sender_type: ChatRole.AGENT,
  created_at: new Date().toISOString(),
} satisfies ChatMessage;

const useChatState = createGlobalState(() => {
  const { selectedApplication } = useApplicationsState();
  const { isLoggedIn } = useAuthState();
  const { welcomeScreenTab } = usePlatformScreenState();

  const chatChannel = ref<ChatPrivateChannel | ChatPublicChannel | null>(null);
  const applicationId = ref<Application["id"] | null>(null);
  const sessionId = ref<ChatSession["id"] | null>(null);
  const messages = ref<ChatMessage[]>([]);
  const agentIsTyping = ref(false);
  const isUploading = ref(false);
  const fileErrors = ref<Set<File>>(new Set());

  const hasSession = computed(() => !!sessionId.value);
  const hasMessages = computed(() => messages.value.length > 0);

  const isValidStateForWeclomeScreenChat = computed(() => {
    return !isLoggedIn.value && welcomeScreenTab.value?.mode === WelcomePageType.AiAgent;
  });

  const { executeFetch: startNewSession } = usePromiseWrapper(async (settings: SessionSettings) => {
    const chatSessionService = new ChatSessionHttpService();

    const newSession = await chatSessionService.create({
      agent_id: settings.agentId,
      application_id: settings.applicationId,
    });

    sessionId.value = newSession.id;
    agentIsTyping.value = false;
    messages.value = [];

    if (settings.initialMessage) {
      messages.value.push(settings.initialMessage);
    }
  });

  const uploadFiles = async (
    files: File[],
    sessionId: number,
    chatSessionService: ChatSessionHttpService
  ): Promise<{ fileBlocks: MessageContentBlock[]; failedFiles: File[] }> => {
    const fileBlocks: MessageContentBlock[] = [];
    const failedFiles: File[] = [];

    for (const file of files) {
      try {
        const { file_id } = await chatSessionService.uploadFile(sessionId, file);
        fileBlocks.push(createFileBlock(file_id, file.name));
      } catch (error) {
        failedFiles.push(file);
        fileErrors.value.add(file);
      }
    }

    return { fileBlocks, failedFiles };
  };

  const addUserMessage = (messageContent: MessageContent): void => {
    messages.value.push({
      content: messageContent,
      sender_type: ChatRole.USER,
      created_at: formatISO(Date.now()),
    });
  };

  const sendMessage = async (
    prompt: string,
    files: File[] = []
  ): Promise<{ success: boolean; failedFiles?: File[] }> => {
    if (!sessionId.value) {
      return { success: false };
    }

    try {
      isUploading.value = true;
      fileErrors.value.clear();

      const chatSessionService = new ChatSessionHttpService();
      const { fileBlocks, failedFiles } = await uploadFiles(
        files,
        sessionId.value,
        chatSessionService
      );

      if (failedFiles.length) {
        return { success: false, failedFiles };
      }

      const messageContent = buildMessageContent(prompt, fileBlocks);

      if (!messageContent.content.length) {
        return { success: false };
      }

      addUserMessage(messageContent);
      await chatSessionService.prompt(sessionId.value, { content: messageContent });

      agentIsTyping.value = true;
      return { success: true };
    } catch (error) {
      return { success: false };
    } finally {
      isUploading.value = false;
    }
  };

  /**
   * @description Attempt to resume the latest (by created_at) chat session for an application.
   *  If none exist, start a new chat session with the given applications id and agent id.
   */
  const startOrResumeSessionFromApplication = async (newApplicaton: Application) => {
    const applicationAgentId = newApplicaton.chat?.agent_id;

    if (!applicationAgentId || applicationId.value === newApplicaton.id) {
      return;
    }

    const chatSessionService = new ChatSessionHttpService();
    const inProgressSessions = await chatSessionService.showAll({
      application_id: newApplicaton.id,
      finished: 0,
    });

    if (!inProgressSessions.length) {
      await startNewSession({
        applicationId: newApplicaton.id,
        agentId: applicationAgentId,
        initialMessage: welcomeMessage,
      });
      return;
    }

    const mostRecentSession = inProgressSessions.reduce((latest, session) => {
      return compareDesc(parseISO(session.created_at), parseISO(latest.created_at)) === -1
        ? latest
        : session;
    }, inProgressSessions[0]);

    const latestInProgressSession = await chatSessionService.show(mostRecentSession.id);

    sessionId.value = latestInProgressSession.id;
    messages.value = [welcomeMessage, ...latestInProgressSession.messages];
  };

  watch(
    () => [sessionId.value, isLoggedIn.value],
    ([newSessionId, newIsLoggedIn]) => {
      const sessionId = newSessionId as number;
      const isLoggedIn = newIsLoggedIn as boolean;

      if (chatChannel.value) {
        chatChannel.value.leave();
      }

      if (!newSessionId) {
        chatChannel.value = null;
        return;
      }

      chatChannel.value = isLoggedIn
        ? new ChatPrivateChannel(sessionId)
        : new ChatPublicChannel(sessionId);

      chatChannel.value.subscribeTo(
        new AiAgentResponseReceivedEvent((event) => {
          agentIsTyping.value = false;
          messages.value.push({
            content: {
              version: "1.0.0",
              content: [
                {
                  type: "text",
                  text: event.message,
                },
              ],
            },
            sender_type: ChatRole.AGENT,
          });
        })
      );
    },
    { immediate: true }
  );

  watchEffect(async () => {
    if (isValidStateForWeclomeScreenChat.value) {
      const agentId = welcomeScreenTab.value?.agent_id;

      if (agentId) {
        await startNewSession({ agentId });
      }
      return;
    }

    if (selectedApplication.value) {
      await startOrResumeSessionFromApplication(selectedApplication.value);
      return;
    }
  });

  return {
    agentIsTyping,
    fileErrors,
    hasSession,
    isUploading,
    messages,
    hasMessages,
    sendMessage,
    startNewSession,
    startOrResumeSessionFromApplication,
  };
});

export { useChatState };
